/**
 * @file src/helpers/httpClient.ts
 * @exports http, registerVidHttpApiKeyResolver
 * @summary Shared axios instance for Cortex REST calls, adding device-type and vID session API key headers.
 * @remarks `src/helpers` sits in an import cycle (`httpClient` → `generated/graphql` → `graphql/fetcher` →
 *          `shame/shame-atoms` → … → `manager`), so `Manager`'s constructor can call
 *          `registerVidHttpApiKeyResolver` before this module's body has run. The resolver is therefore held in a
 *          `var`: it is hoisted and initialized to `undefined` when the module is instantiated, so registration
 *          works at any point. A `let`/`const` would be in the temporal dead zone and throw
 *          `Cannot access 'resolveVidSessionApiKey' before initialization`. Do not give it an initializer either —
 *          this module's body runs *after* registration, and an initializer would overwrite the registered resolver.
 */

import axios, { AxiosHeaders } from "axios";
import { env } from "src/environment";
import { DeviceTypeEnum } from "src/generated/graphql";
import { isMobileBrowser } from "src/views/VID/views/helpers/isMobileBrowser";
import { cortexApiKeyHeaders } from "./cortexApiKeyHeaders";

var resolveVidSessionApiKey: (() => string | undefined) | undefined;

/**
 * Registers where `http` reads the vID session API key for REST calls.
 * Wired from `Manager` so this module never imports `manager` (avoids circular imports).
 */
export function registerVidHttpApiKeyResolver(resolve: () => string | undefined): void {
	resolveVidSessionApiKey = resolve;
}

/**
 * Shared axios instance for all HTTP requests.
 * Includes device type header for tracking mobile vs web usage.
 * Adds `apikey` from the vID session when the caller did not set it (see `registerVidHttpApiKeyResolver`).
 */
export const http = axios.create({
	baseURL: env.SERVER_URL,
	withCredentials: true,
	headers: {
		"x-device-type": isMobileBrowser() ? DeviceTypeEnum.WebMobile : DeviceTypeEnum.Web,
	},
});

http.interceptors.request.use((config) => {
	const extra = cortexApiKeyHeaders(resolveVidSessionApiKey?.());
	if (Object.keys(extra).length === 0) {
		return config;
	}
	const headers = AxiosHeaders.from(config.headers ?? {});
	if (headers.has("apikey")) {
		return config;
	}
	Object.assign(headers, extra);
	config.headers = headers;
	return config;
});
